/* ISQM form pages (firm ISQM 1 form, engagement ISQM 2 form) and their section bodies — moved out of
   pages/ISQM.jsx in split step 5b (28-Oct-2026), unchanged. */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Info,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";


// ======================================================================
// Individual firm-level ISQM 1 form
// ======================================================================
export function ISQMFirmForm() {
  const { formId } = useParams();
  return <ISQMFormPage scope="firm" formId={formId} />;
}


// ======================================================================
// Individual engagement-level ISQM 2 form
// ======================================================================
export function ISQMEngForm() {
  const { engId, formId } = useParams();
  return <ISQMFormPage scope="eng" engId={engId} formId={formId} />;
}


// ======================================================================
// Shared form page — renders checklist / register / signoff / freetext
// sections in exactly the shape the Word doc defines. Persists the full
// answer tree via PATCH; every save is Audit-Trail logged server-side.
// ======================================================================
function ISQMFormPage({ scope, engId, formId }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [payload, setPayload] = useState(null);
  const [answers, setAnswers] = useState({});
  const [evidence, setEvidence] = useState({});
  const [saving, setSaving] = useState(false);

  const endpoint = scope === "firm"
    ? `/isqm/firm/${formId}`
    : `/isqm/engagement/${engId}/${formId}`;

  useEffect(() => {
    api.get(endpoint).then((r) => {
      setPayload(r.data);
      setAnswers(r.data.state?.answers || {});
      setEvidence(r.data.state?.evidence || {});
    }).catch((e) => toast.error(toErrMsg(e, t("isqmPage.formLoadFailed"))));
    // eslint-disable-next-line
  }, [endpoint]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(endpoint, { answers, evidence });
      toast.success(t("isqmPage.toastSaved"));
    } catch (e) { toast.error(toErrMsg(e, t("isqmPage.toastSaveFailed"))); }
    finally { setSaving(false); }
  };

  if (!payload) return <div className="p-8 text-sm text-muted-foreground">{t("isqmPage.formLoading")}</div>;
  const spec = payload.spec;
  const secs = spec.sections || [];

  return (
    <div className="space-y-4" data-testid={`isqm-form-${scope}`}>
      <div>
        <button onClick={() => nav("/isqm")} className="text-xs text-emerald-600 hover:underline">{t("isqmPage.formBack")}</button>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          <span className="me-2 font-mono text-base text-slate-500">{spec.number}</span>
          {spec.name}
        </h1>
        <p className="text-sm text-muted-foreground">{spec.isa} · {spec.category}</p>
        {spec.intro && (
          <p className="mt-3 rounded-md border-s-4 border-emerald-400 bg-emerald-50/60 p-3 text-[12.5px] leading-relaxed text-slate-700 dark:bg-emerald-950/30 dark:text-slate-200"
             data-testid="isqm-intro">
            {spec.intro}
          </p>
        )}
      </div>

      {secs.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            <Info className="me-1 inline h-3 w-3" />
            {t("isqmPage.formContentPendingPre")}
            <span className="mx-1 rounded bg-slate-100 px-1 font-mono text-[11px]">{spec.doc_file}</span>
            {t("isqmPage.formContentPendingPost")}
          </CardContent>
        </Card>
      )}

      {secs.map((sec) => (
        <ISQMSection
          key={sec.id}
          section={sec}
          answers={answers[sec.id] || {}}
          evidence={evidence[sec.id] || ""}
          onAnswersChange={(v) => setAnswers({ ...answers, [sec.id]: v })}
          onEvidenceChange={(v) => setEvidence({ ...evidence, [sec.id]: v })}
        />
      ))}

      {secs.length > 0 && (
        <div className="sticky bottom-2 flex justify-end">
          <Button onClick={save} disabled={saving}
                   className="bg-slate-900 hover:bg-slate-800 shadow-lg"
                   data-testid="isqm-save">
            {saving ? t("isqmPage.formSaving") : t("isqmPage.formSave")}
          </Button>
        </div>
      )}
    </div>
  );
}


function ISQMSection({ section, answers, evidence, onAnswersChange, onEvidenceChange }) {
  const { t } = useI18n();
  return (
    <Card data-testid={`isqm-section-${section.id}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{section.name}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {section.type === "checklist" && (
          <ChecklistBody items={section.items || []} value={answers}
                          onChange={onAnswersChange} sid={section.id} />
        )}
        {section.type === "register" && (
          <RegisterBody columns={section.columns || []}
                         minRows={section.min_rows || 3}
                         value={answers} onChange={onAnswersChange}
                         sid={section.id} />
        )}
        {section.type === "signoff" && (
          <SignoffBody fields={section.fields || []} value={answers}
                        onChange={onAnswersChange} sid={section.id} />
        )}
        {section.type === "freetext" && (
          <textarea rows={4}
                    className="w-full rounded-md border p-2 text-sm dark:bg-slate-900"
                    placeholder={t("isqmPage.formFreetextPlaceholder")}
                    value={answers.note || ""}
                    onChange={(e) => onAnswersChange({ note: e.target.value })}
                    data-testid={`isqm-freetext-${section.id}`} />
        )}
        {section.evidence_prompt && (
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {section.evidence_prompt}
            </Label>
            <textarea rows={2}
                       className="mt-1 w-full rounded-md border p-2 text-sm dark:bg-slate-900"
                       value={evidence || ""}
                       onChange={(e) => onEvidenceChange(e.target.value)}
                       data-testid={`isqm-evidence-${section.id}`} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function ChecklistBody({ items, value, onChange, sid }) {
  const colW = useResizableColumns({ storageKey: "isqm-checklist-body", defaults: [100, 240, 100, 110], mins: [64, 130, 64, 72] });   // Step 11d · add-on
  const { t } = useI18n();
  const setItem = (idx, patch) => {
    const cur = value[idx] || {};
    onChange({ ...value, [idx]: { ...cur, ...patch } });
  };
  return (
    <div className="rounded-md border">
      <ResizableTable cols={colW} wrap className="w-full text-sm">
        <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
          <tr>
            <ResizableTh index={0} className="w-10 px-2 py-2 text-center">#</ResizableTh>
            <ResizableTh index={1} className="px-3 py-2 text-start">{t("isqmPage.chkColItem")}</ResizableTh>
            <ResizableTh index={2} className="w-40 px-3 py-2 text-center">{t("isqmPage.chkColResponse")}</ResizableTh>
            <ResizableTh index={3} className="w-40 px-3 py-2 text-start">{t("isqmPage.chkColRef")}</ResizableTh>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => {
            const cur = value[idx] || {};
            return (
              <tr key={idx} className="border-b align-top" data-testid={`${sid}-row-${idx}`}>
                <td className="px-2 py-2 text-center font-mono text-xs tabular-nums">{idx + 1}</td>
                <td className="px-3 py-2 leading-snug">{it.text}</td>
                <td className="px-3 py-2 text-center">
                  <div className="inline-flex gap-1 rounded-md border bg-slate-50 p-0.5 text-[11px] dark:bg-slate-900">
                    {["yes", "no", "na"].map((opt) => (
                      <button key={opt} type="button"
                        onClick={() => setItem(idx, { response: opt })}
                        className={`rounded px-2 py-1 font-medium ${
                          cur.response === opt
                            ? (opt === "yes" ? "bg-emerald-600 text-white"
                               : opt === "no" ? "bg-red-600 text-white"
                               : "bg-slate-600 text-white")
                            : "text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                        }`}
                        data-testid={`${sid}-row-${idx}-${opt}`}>
                        {opt === "na" ? t("isqmPage.chkResponseNa") : opt.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{it.ref}</td>
              </tr>
            );
          })}
        </tbody>
      </ResizableTable>
    </div>
  );
}


function RegisterBody({ columns, minRows, value, onChange, sid }) {
  const { t } = useI18n();
  const regCols = useResizableColumns({ storageKey: `isqm-register-${sid}`, defaults: [...columns.map(() => 200), 48], mins: [...columns.map(() => 110), 40] });   // Step 11d · add-on
  const rows = Array.isArray(value.rows) ? value.rows : [];
  while (rows.length < minRows) rows.push(columns.map(() => ""));
  const setCell = (r, c, v) => {
    const next = rows.map((row) => [...row]);
    while (next.length <= r) next.push(columns.map(() => ""));
    next[r][c] = v;
    onChange({ rows: next });
  };
  const addRow = () => onChange({ rows: [...rows, columns.map(() => "")] });
  const removeRow = (r) => {
    const next = rows.filter((_, i) => i !== r);
    onChange({ rows: next });
  };
  return (
    <div>
      <div className="overflow-x-auto rounded-md border">
        <ResizableTable cols={regCols} className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
            <tr>
              {columns.map((c, i) => <ResizableTh key={c} index={i} className="px-3 py-2 text-start">{c}</ResizableTh>)}
              <ResizableTh index={columns.length} className="w-10 px-2 py-2"></ResizableTh>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r} className="border-b" data-testid={`${sid}-reg-row-${r}`}>
                {columns.map((_, c) => (
                  <td key={c} className="px-2 py-1">
                    <Input value={row[c] || ""} onChange={(e) => setCell(r, c, e.target.value)}
                           className="h-8 text-xs"
                           data-testid={`${sid}-reg-${r}-${c}`} />
                  </td>
                ))}
                <td className="px-1 py-1 text-center">
                  <button type="button" onClick={() => removeRow(r)}
                          className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40"
                          title={t("isqmPage.regRemoveRow")}
                          data-testid={`${sid}-reg-remove-${r}`}>
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </ResizableTable>
      </div>
      <Button size="sm" variant="outline" onClick={addRow} className="mt-2"
              data-testid={`${sid}-reg-add`}>{t("isqmPage.regAddRow")}</Button>
    </div>
  );
}


function SignoffBody({ fields, value, onChange, sid }) {
  const set = (label, v) => onChange({ ...value, [label]: v });
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
      {fields.map((f) => (
        <div key={f.label}>
          <Label className="text-[11px]">{f.label}</Label>
          <Input value={value[f.label] || ""} onChange={(e) => set(f.label, e.target.value)}
                 data-testid={`${sid}-signoff-${f.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
        </div>
      ))}
    </div>
  );
}
